/**
 * Ready-made query documents. Field names and arguments match `schema.graphql`
 * exactly; selection sets are composed from the shared fragments.
 */
import {
	composeDocument,
	MediaFields,
	NavItemFields,
	PageBlocks,
	SeoFields,
	ServiceCardFields,
	ServiceFull,
	CompanyCardFields,
	CompanyFull,
	CaseStudyCardFields,
	CaseStudyFull,
	ArticleCardFields,
	ArticleFull,
	ResourceFull,
	WebinarCardFields,
	FormDefinitionFields,
} from "./fragments.js";

const SITE = composeDocument(
	/* GraphQL */ `
		query Site($key: String) {
			site(key: $key) {
				id
				key
				name
				primaryDomain
				additionalDomains
				defaultLocale
				supportedLocales
				theme {
					id
					tokenOverrides
					defaultThemePreference
				}
				heroImage {
					...MediaFields
				}
			}
		}
	`,
	[MediaFields],
);

const NAVIGATION = composeDocument(
	/* GraphQL */ `
		query Navigation($site: String!, $locale: Locale!) {
			navigation(site: $site, locale: $locale) {
				id
				primary {
					...NavItemFields
					children {
						...NavItemFields
						children {
							...NavItemFields
						}
					}
				}
				utility {
					...NavItemFields
					children {
						...NavItemFields
					}
				}
			}
		}
	`,
	[NavItemFields],
);

const FOOTER = composeDocument(
	/* GraphQL */ `
		query Footer($site: String!, $locale: Locale!) {
			footer(site: $site, locale: $locale) {
				id
				columns {
					heading
					items {
						...NavItemFields
					}
				}
				legalLinks {
					...NavItemFields
				}
				copyright
			}
		}
	`,
	[NavItemFields],
);

const PAGE = composeDocument(
	/* GraphQL */ `
		query Page($site: String!, $locale: Locale!, $slug: String!, $preview: Boolean) {
			page(site: $site, locale: $locale, slug: $slug, preview: $preview) {
				id
				documentId
				slug
				status
				locale
				isDemo
				publishedAt
				title
				blocks {
					...PageBlocks
				}
				seo {
					...SeoFields
				}
			}
		}
	`,
	[PageBlocks, SeoFields],
);

const SERVICE_CATEGORIES = composeDocument(
	/* GraphQL */ `
		query ServiceCategories($site: String!, $locale: Locale!) {
			serviceCategories(site: $site, locale: $locale) {
				id
				documentId
				slug
				status
				locale
				isDemo
				publishedAt
				title
				summary
				icon
				order
				services {
					...ServiceCardFields
				}
			}
		}
	`,
	[ServiceCardFields],
);

const SERVICES = composeDocument(
	/* GraphQL */ `
		query Services(
			$site: String!
			$locale: Locale!
			$categorySlug: String
			$materialStream: String
			$first: Int
			$after: String
			$preview: Boolean
		) {
			services(
				site: $site
				locale: $locale
				categorySlug: $categorySlug
				materialStream: $materialStream
				first: $first
				after: $after
				preview: $preview
			) {
				edges {
					cursor
					node {
						...ServiceCardFields
					}
				}
				pageInfo {
					hasNextPage
					endCursor
					total
				}
			}
		}
	`,
	[ServiceCardFields],
);

const SERVICE = composeDocument(
	/* GraphQL */ `
		query Service($site: String!, $locale: Locale!, $slug: String!, $preview: Boolean) {
			service(site: $site, locale: $locale, slug: $slug, preview: $preview) {
				...ServiceFull
			}
		}
	`,
	[ServiceFull],
);

const COMPANIES = composeDocument(
	/* GraphQL */ `
		query Companies($site: String!, $locale: Locale!) {
			companies(site: $site, locale: $locale) {
				...CompanyCardFields
			}
		}
	`,
	[CompanyCardFields],
);

const COMPANY = composeDocument(
	/* GraphQL */ `
		query Company($site: String!, $locale: Locale!, $slug: String!, $preview: Boolean) {
			company(site: $site, locale: $locale, slug: $slug, preview: $preview) {
				...CompanyFull
			}
		}
	`,
	[CompanyFull],
);

const CASE_STUDIES = composeDocument(
	/* GraphQL */ `
		query CaseStudies($site: String!, $locale: Locale!, $serviceSlug: String) {
			caseStudies(site: $site, locale: $locale, serviceSlug: $serviceSlug) {
				...CaseStudyCardFields
			}
		}
	`,
	[CaseStudyCardFields],
);

const CASE_STUDY = composeDocument(
	/* GraphQL */ `
		query CaseStudy($site: String!, $locale: Locale!, $slug: String!, $preview: Boolean) {
			caseStudy(site: $site, locale: $locale, slug: $slug, preview: $preview) {
				...CaseStudyFull
			}
		}
	`,
	[CaseStudyFull],
);

const ARTICLES = composeDocument(
	/* GraphQL */ `
		query Articles($site: String!, $locale: Locale!, $tag: String, $first: Int, $after: String) {
			articles(site: $site, locale: $locale, tag: $tag, first: $first, after: $after) {
				edges {
					cursor
					node {
						...ArticleCardFields
					}
				}
				pageInfo {
					hasNextPage
					endCursor
					total
				}
			}
		}
	`,
	[ArticleCardFields],
);

const ARTICLE = composeDocument(
	/* GraphQL */ `
		query Article($site: String!, $locale: Locale!, $slug: String!, $preview: Boolean) {
			article(site: $site, locale: $locale, slug: $slug, preview: $preview) {
				...ArticleFull
			}
		}
	`,
	[ArticleFull],
);

const RESOURCES = composeDocument(
	/* GraphQL */ `
		query Resources($site: String!, $locale: Locale!, $kind: String) {
			resources(site: $site, locale: $locale, kind: $kind) {
				...ResourceFull
			}
		}
	`,
	[ResourceFull],
);

const RESOURCE = composeDocument(
	/* GraphQL */ `
		query Resource($site: String!, $locale: Locale!, $slug: String!) {
			resource(site: $site, locale: $locale, slug: $slug) {
				...ResourceFull
			}
		}
	`,
	[ResourceFull],
);

const WEBINARS = composeDocument(
	/* GraphQL */ `
		query Webinars($site: String!, $locale: Locale!, $upcoming: Boolean) {
			webinars(site: $site, locale: $locale, upcoming: $upcoming) {
				...WebinarCardFields
			}
		}
	`,
	[WebinarCardFields],
);

const FORM_DEFINITION = composeDocument(
	/* GraphQL */ `
		query FormDefinition($site: String!, $locale: Locale!, $key: String!) {
			formDefinition(site: $site, locale: $locale, key: $key) {
				...FormDefinitionFields
			}
		}
	`,
	[FormDefinitionFields],
);

const SEARCH = /* GraphQL */ `
	query Search(
		$site: String!
		$locale: Locale!
		$query: String!
		$types: [SearchableContentType!]
		$first: Int
	) {
		search(site: $site, locale: $locale, query: $query, types: $types, first: $first) {
			query
			total
			tookMs
			suggestions
			hits {
				id
				contentType
				locale
				title
				snippet
				url
				score
			}
		}
	}
`;

const HEALTH = /* GraphQL */ `
	query Health {
		health {
			ok
			version
			services {
				name
				ok
			}
		}
	}
`;

/** All ready-made query documents, keyed by name. */
export const queries = {
	SITE,
	NAVIGATION,
	FOOTER,
	PAGE,
	SERVICE_CATEGORIES,
	SERVICES,
	SERVICE,
	COMPANIES,
	COMPANY,
	CASE_STUDIES,
	CASE_STUDY,
	ARTICLES,
	ARTICLE,
	RESOURCES,
	RESOURCE,
	WEBINARS,
	FORM_DEFINITION,
	SEARCH,
	HEALTH,
} as const;

/** Union of available query names in {@link queries}. */
export type QueryName = keyof typeof queries;
